import { Context, Controller } from '@hotwired/stimulus';
import { toggleAttribute } from 'src/utils/dom-toggle';

const DISABLE_ALL_TIMEZONE_INSPECT = true;

/**

 */
export default class TimezoneInspectController extends Controller {
  public static targets = [];
  public static values = {
    datetime: String,
  };

  declare private formatValue: string;
  declare private datetimeValue: string;

  declare private hasDatetimeValue: boolean;

  private connected: boolean = false;
  private inflated: HTMLDivElement | undefined;
  private luxon: any;

  private get datetime(): Date {
    if (this.hasDatetimeValue) {
      return new Date(this.datetimeValue);
    } else {
      return new Date(
        this.element.querySelector('time[datetime]')!.getAttribute('datetime')!,
      );
    }
  }

  private get localTarget(): HTMLElement {
    return this.inflated!.querySelector(
      '[data-timezone-inspect-target="local"]',
    )!;
  }

  private get metaTarget(): HTMLElement {
    return this.inflated!.querySelector(
      '[data-timezone-inspect-target="meta"]',
    )!;
  }

  private get selectTarget(): HTMLSelectElement {
    return this.inflated!.querySelector(
      '[data-timezone-inspect-target="select"]',
    )!;
  }

  public constructor(context: Context) {
    super(context);

    this.onChangeTimezone = this.onChangeTimezone.bind(this);
  }

  public connect(): void {
    if (!supportedNatively()) {
      toggleAttribute('disabled', this.element, true);
      return;
    }

    // TODO: DISABLE FOR NOW
    if (DISABLE_ALL_TIMEZONE_INSPECT) {
      toggleAttribute('disabled', this.element, true);
      return;
    }

    this.connected = true;

    // Already inflated
    if (this.element.closest('[data-controller~="popover"]')) {
      this.element.setAttribute('data-popover-target', 'toggler');
      this.element.setAttribute(
        'data-action',
        `
        focus->popover#onReferenceUp
        blur->popover#onReferenceDownNextFrame
        mouseenter->popover#onDelayedReferenceUp
        mouseleave->popover#onDelayedReferenceDown
      `,
      );

      this.inflated =
        this.element
          .closest('[data-controller~="popover"]')
          ?.querySelector('[data-popover-target="popover"]') ?? undefined;
    } else {
      const placer = document.createElement('div');
      const oldParent = this.element.parentElement!;
      oldParent.replaceChild(placer, this.element);

      const newParent = inflate();

      // This will trigger a disconnect and connect
      newParent.prepend(this.element);
      placer.replaceWith(newParent);
      return;
    }

    const promise =
      supported.luxon || import('luxon').then(({ DateTime }) => DateTime);
    if (!supported.luxon) {
      supported.luxon = promise;
    }

    void promise.then((DateTime) => {
      if (!this.connected) {
        return;
      }

      this.luxon = DateTime;

      const dateTime = this.luxon.fromISO(this.datetime.toISOString());
      this.localTarget.textContent = `${dateTime.toLocaleString(
        this.luxon.DATETIME_FULL,
      )}`;

      this.metaTarget.innerHTML = `
        <dl>
          <div>
            <dt>UTC</dt>
            <dd>${dateTime
              .setZone('UTC')
              .toLocaleString(this.luxon.DATETIME_FULL)}</dd>
          </div>
          <div>
            <dt>Europe/London</dt>
            <dd>${dateTime
              .setZone('Europe/London')
              .toLocaleString(this.luxon.DATETIME_FULL)}</dd>
          </div>
          <div>
            <dt>Europe/Amsterdam</dt>
            <dd>${dateTime
              .setZone('Europe/Amsterdam')
              .toLocaleString(this.luxon.DATETIME_FULL)}</dd>
          </div>
        </dl>
      `;

      // this.selectTarget.addEventListener('input', this.onChangeTimezone);
      // console.log(this.selectTarget);
    });
  }

  public disconnect(): void {
    this.connected = false;

    // if (this.selectTarget) {
    //   this.selectTarget.removeEventListener('input', this.onChangeTimezone);
    // }

    this.inflated = undefined;

    this.element.setAttribute(
      'data-controller',
      this.element
        .getAttribute('data-controller')!
        .split(' ')
        .filter((x) => x !== 'popover')
        .join(' '),
    );
  }

  private onChangeTimezone(e: Event) {
    const value = (e.target as HTMLSelectElement).value;

    const dateTime = this.luxon
      .fromISO(this.datetime.toISOString())
      .setZone(value);

    this.localTarget.textContent = `${dateTime.toLocaleString(
      this.luxon.DATETIME_FULL,
    )}`;

    this.inflated?.focus();
  }
}

const supported = {
  natively: undefined as undefined | boolean,
  luxon: undefined as undefined | Promise<typeof import('luxon').DateTime>,
};

function supportedNatively() {
  if (typeof supported.natively === 'boolean') {
    return supported.natively;
  }

  try {
    const locale = new Intl.Locale('en-GB');
    if ('getTimeZones' in locale && typeof locale.getTimeZones === 'function') {
      const timezones = locale.getTimeZones();
      if (Array.isArray(timezones) && timezones.includes('Europe/London')) {
        supported.natively = true;
        return true;
      }

      throw new Error(
        'Expected Europe/London in Intl.Locale#getTimeZones, actual: ' +
          JSON.stringify(timezones ?? null),
      );
    }

    throw new Error('Expected getTimeZones to exist on Intl.Locale');
  } catch (_) {
    supported.natively = false;
    return false;
  }
}

const TIMEZONE_INSPECT_DIALOG_HTML = `
<div
  data-controller="popover"
  data-popover-align-value="start"
  data-popover-side-value="outside-bottom"
  class="relative flex">

  <div
    data-view="~/timezone_inspect_controller"
    class="popover --light-dark --w-80 z-10"
    data-popover-target="popover"
    hidden
    tabindex="-1">

    <div class="context__dialog">

      <div class="context__header">
        <p class="context__title">
          <span class="flex w-full">
            <span data-timezone-inspect-target="local">{{localTime}}</span>
          </span>
        </p>
      </div>

      <div class="context__meta" data-timezone-inspect-target="meta">

      </div>
    </div>
  </div>
</div>
`;

function inflate() {
  const element = document.createElement('div');
  element.innerHTML = TIMEZONE_INSPECT_DIALOG_HTML;
  return element.firstElementChild as HTMLDivElement;
}
